import { Link } from 'wouter';
import { routes } from '../../lib/assessment/routes';
import { cn } from '../../lib/utils';

/**
 * The brand mark.
 *
 * V2-018C-2 replaces the placeholder wordmark with the legacy logo asset. The
 * file, the `srcSet` pair, the `sizes` hints, and the heights are the legacy
 * values: `h-16` in the header at `sizes="345px"`, and `h-6` at 60% opacity
 * with lazy loading in the footer at `sizes="130px"`. That settles the two
 * open questions about logo asset and mark height by migration rather than by
 * design decision.
 *
 * The asset is served from this repository's `public/`, so no external origin
 * is referenced and the content policy is unchanged.
 */
export type BrandMarkProps = {
  /** `header` is the primary identity and home link; `footer` is the
   *  reduced-prominence repetition. */
  placement?: 'header' | 'footer';
  className?: string;
};

const SRC = `${import.meta.env.BASE_URL}logo-white-v2.webp`;
const SRC_SET =
  `${import.meta.env.BASE_URL}logo-white-v2@1x.webp 938w, ` +
  `${import.meta.env.BASE_URL}logo-white-v2.webp 1876w`;

export function BrandMark({ placement = 'header', className }: BrandMarkProps) {
  const isHeader = placement === 'header';

  return (
    <Link
      href={routes.home}
      data-testid="brand-mark"
      className={cn(
        'inline-flex items-center',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        className,
      )}
    >
      <img
        src={SRC}
        srcSet={SRC_SET}
        sizes={isHeader ? '345px' : '130px'}
        alt="High Performer OS"
        className={isHeader ? 'h-16 w-auto' : 'h-6 w-auto opacity-60'}
        {...(isHeader ? {} : { loading: 'lazy' as const })}
      />
    </Link>
  );
}
